import { PropsWithChildren, useCallback, useEffect, useState } from "react";

import { ApolloClient, useApolloClient } from "@apollo/client";
import { FragmentDefinitionNode } from "graphql";
import _ from "lodash";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";
import { useRouter } from "next/router";

import createProvider from "@/providerFactories/createProvider";
import { generateCacheId } from "@/utils/generateCacheId";

import { LAYOUT_DATA_FRAGMENTS, LayoutDataProviderType, useLayoutData } from "./useLayoutData";

const LABEL_FINDERS: {
  [key: string]: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    arg0: any,
    key: string,
    searchParams: NextParsedUrlQuery,
    layoutData?: LayoutDataProviderType
  ) => string;
} = {
  buildId: result => {
    // @todo remove this when we give build a relative number name
    return result?.unhashedDatabaseId;
  },
  clusterId: result => {
    return result?.context;
  },
  deployId: result => {
    return result?.unhashedDatabaseId;
  },
  databaseId: result => {
    const poolable = result?.poolable;
    return poolable?.name || "";
  },
  externalRegistryId: result => {
    return result?.host;
  },
  presetId: result => {
    const poolable = result?.poolable;
    return poolable?.handle || "";
  },
  integrationSlug: (result, key, _searchParams, layoutData) => {
    const layoutName = layoutData?.layoutIntegration?.name;
    if (layoutName && _.snakeCase(layoutName) === key) return layoutName;
    if (result) return result.name;
    switch (key) {
      case "open_ai":
        return "OpenAI";
      case "planet_scale":
        return "PlanetScale";
      default:
        return _.startCase(key);
    }
  },
  cloudProviderType: (_result, key) => {
    switch (key) {
      case "aws":
      case "gcp":
        return key.toUpperCase();
      case "release":
        return "Release";
      default:
        return "";
    }
  },
  memberType: (_result, key) => {
    return _.startCase(key);
  },
  agentId: (_result, key) => key,
  sessionId: (_result, key) => key,
  templateId: (_result, key) => key,
  vaultId: (_result, key) => key,
  workflowRef: (_result, key) => decodeURIComponent(key),
  runId: (_result, key) => key,
};

const lookupLabel = (
  apolloClient: ApolloClient<unknown>,
  searchParams: NextParsedUrlQuery,
  key: string,
  type: string,
  layoutData?: LayoutDataProviderType
) => {
  const finder = LABEL_FINDERS[type];
  const fragment = LAYOUT_DATA_FRAGMENTS[type as keyof typeof LAYOUT_DATA_FRAGMENTS];
  const cacheId = generateCacheId(type, searchParams);
  if (!cacheId) {
    // No cache mapping — run the finder with null result if one exists
    return finder ? finder(null, key, searchParams, layoutData) : undefined;
  }
  const query = {
    id: cacheId,
    fragment: fragment,
    fragmentName: fragment && (fragment.definitions[0] as FragmentDefinitionNode).name.value,
  };
  const result = cacheId && fragment ? apolloClient.readFragment(query) : null;
  return finder ? finder(result, key, searchParams, layoutData) : result?.name;
};

export type BreadcrumbData = {
  breadcrumb: string | undefined;
  href: string;
};

type BreadcrumbsProviderType = {
  breadcrumbs: BreadcrumbData[];
  updateBreadcrumbs: () => void;
  setBreadcrumbLabel: (href: string, label: string) => void;
};

export const initialData: BreadcrumbsProviderType = {
  breadcrumbs: [] as BreadcrumbData[],
} as BreadcrumbsProviderType;

const useBreadcrumbsProviderHook = () => {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const layoutData = useLayoutData();

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbData[]>([]);

  const updateBreadcrumbs = useCallback(() => {
    const linkPathTemplate = router.pathname.split("?")[0].split("/").slice(0);
    const linkPath = router.asPath.split("?")[0].split("/").slice(0);
    linkPath.shift();
    linkPathTemplate.shift();
    const pathArray = linkPath.map(function (path, i) {
      const match = linkPathTemplate[i]?.match(/\[(.*)]/);
      return {
        breadcrumb: match
          ? lookupLabel(apolloClient, router.query, path, match[1], layoutData)
          : _.startCase(path),
        href: "/" + linkPath.slice(0, i + 1).join("/"),
      };
    });
    setBreadcrumbs(pathArray);
  }, [apolloClient, layoutData, router, setBreadcrumbs]);

  const setBreadcrumbLabel = useCallback((href: string, label: string) => {
    setBreadcrumbs(prev => prev.map(b => (b.href === href ? { ...b, breadcrumb: label } : b)));
  }, []);

  useEffect(() => {
    if (router && layoutData) {
      updateBreadcrumbs();
    }
  }, [layoutData, router, updateBreadcrumbs]);

  return {
    breadcrumbs,
    updateBreadcrumbs,
    setBreadcrumbLabel,
  };
};

export const [useBreadcrumbs, BreadcrumbsProvider, BreadcrumbsConsumer] = createProvider<
  BreadcrumbsProviderType,
  PropsWithChildren<unknown>
>(initialData, useBreadcrumbsProviderHook, "Breadcrumbs");
